import type { AuthController_signIn_v1Response } from '@repo/api-client';
import { createContext, useContext } from 'react';

/**
 * The seam a completed sign-in is handed to, and what a screen may do with a
 * session once there is one.
 *
 * Deliberately the *shape* and not the thing. The session itself is held by
 * `session-holder.ts`, persisted by `session-storage.ts` — which is where the
 * decision about where the token lives is argued — and bound to React by
 * `SessionProvider`. A screen sees only this.
 *
 * What a screen does **not** do here is decide when a session is over. A
 * session ends twelve hours after it was created or thirty minutes after its
 * last request, whichever comes first, and both are enforced by the server on
 * every request. The client's job is not to enforce that; it is to not be
 * surprised by it.
 */

/**
 * Exactly what `POST /v1/auth/sign-in` answered — the token, its `expiresAt`,
 * and the signed-in user. Taken from the generated response type rather than
 * restated: the token appears in full once, in this response, and a
 * hand-written shape that omitted or misspelt a field would typecheck and lose
 * it on a till.
 */
export type Session = AuthController_signIn_v1Response;

/**
 * Why the session that was here is not here any more.
 *
 * The two endings are identical locally — the token is gone and the next request
 * carries none — and `session-holder.ts` treats them as one event on purpose.
 * They differ in exactly one place: what the sign-in screen says to the person
 * who has just arrived on it. Someone thrown out mid-task by a lapsed session
 * and someone who pressed Sign out have different questions, and a blank form
 * answers neither.
 *
 * It is **not persisted**, and that is the point rather than an omission. A
 * reload is not a session ending, so "your session ended" must not survive one —
 * kept in `sessionStorage` it would greet somebody the following morning.
 */
export type SessionEnding = 'expired' | 'signed-out';

export interface SessionStore {
  /** The signed-in session, or `null` when nobody is signed in. */
  readonly session: Session | null;

  /**
   * How the last session ended, or `null` if none has ended in this tab.
   *
   * Read by the sign-in screen and by nothing else. It says nothing about
   * whether any staff code exists — it is this browser describing its own
   * history — so it cannot re-open the enumeration the API deliberately closed.
   */
  readonly endedBecause: SessionEnding | null;

  /**
   * Say that the ending above has been shown to somebody, so it stops being the
   * answer to "why am I here" for every later arrival at the sign-in screen.
   *
   * Without it the reason is tab-scoped rather than arrival-scoped: sign out
   * once and "You are signed out" greets every subsequent visit to that screen,
   * including a later bounce whose true sentence is a different one. Both
   * statements stay true, which is what makes it easy to miss — it is the line
   * describing the previous trip instead of this one.
   */
  readonly acknowledgeEnding: () => void;

  /**
   * Take a completed sign-in. The only way a session is established.
   *
   * The whole response goes in — the token and the returned user and nothing
   * else. It is never written to a query string, `localStorage`,
   * `sessionStorage` or a cookie by the caller; where it is kept is decided
   * behind this seam, in `session-storage.ts`.
   */
  readonly signedIn: (session: Session) => void;

  /**
   * Take what the API just reported about the signed-in user, after they changed
   * their own credential.
   *
   * The token and its expiry are untouched — changing a password does not end
   * the session that changed it — and the user is replaced wholesale with the
   * server's own answer. Passing the response through is the point: it carries
   * `mustChangeCredential`, and that flag is the server's to set. A screen that
   * concluded "the call succeeded, so the gate must be clear" would be wrong for
   * the case the gate exists for, because a PIN-only change can leave a
   * provisioned password in place and the flag with it.
   */
  readonly credentialChanged: (user: Session['user']) => void;

  /**
   * End the session: `POST /v1/auth/sign-out`, and then clear this browser
   * **whatever that request did**.
   *
   * The order is the point. Somebody who clicked sign-out is signed out of this
   * browser regardless of what the network said — a token left behind because a
   * request timed out is a token left on a shared back-office terminal for the
   * rest of its twelve hours. The request is still made first, and its failure
   * still costs something real: the server-side session outlives the click and
   * stays usable to anyone holding a copy of the token until it lapses on its
   * own. That is the lesser of the two, not nothing.
   *
   * Never rejects, and never hangs: the request is given a few seconds and then
   * abandoned. A refusal and a network that never answers are the same event
   * here, and the second is the one a shared desk at the end of a shift
   * actually produces. There is no failure a caller could usefully handle,
   * because the local half cannot fail and the remote half is bounded.
   */
  readonly signOut: () => Promise<void>;
}

/**
 * Undefined until a provider supplies one, so a screen mounted outside the
 * store fails loudly instead of dropping a token on the floor.
 */
export const SessionContext = createContext<SessionStore | undefined>(undefined);

export function useSession(): SessionStore {
  const store = useContext(SessionContext);

  if (!store) {
    throw new Error('No SessionContext above this component. Provide one at the app root.');
  }

  return store;
}
